import { Input } from '@byecode/ui'
import type {
    ArgumentsSchema,
    AvailableFieldBlockInfo,
    AvailableFilterBlockInfo,
    DataSourceAbstract,
    Field,
    FilterBlockAbstract,
    IndependentAction,
    PageVariableSchema,
    StateSchema,
    TypeInstanceMap,
    ViewType
} from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import React, { useCallback, useMemo } from 'react'
import type { ControllerRenderProps, FieldValues } from 'react-hook-form'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import styled from 'styled-components'

import { placeholderFromInnerType, USER_DATASOURCE } from '../../constants'
import { filterModeIndex } from '../../constants/filter'
import type { FlowNode, Workflow } from '../../types'
import {
    AggregationInnerTypeToFieldType,
    DEFAULT_VALUE_VARIABLE,
    getDateVariableOption,
    getDefaultDataSourceOptions,
    getFilterControllerVariableOption,
    getFormDatasourceOptions,
    getImageVariableOption,
    getInputVariableOption,
    getPageParameterAndVariableOptions,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    getUserDatasourceOptions,
    getViewVariableOption,
    VariableSelect
} from '../'
import { FILTER_ITEM_MAX_WIDTH } from './constant'
import { FILTER_OPERATOR, needNumberParamSystemVariableList, systemMapList } from './constants'
import * as SC from './styles'

interface ParamsVariableProps {
    field?: Field
    formDsId?: string
    dataSource?: DataSourceAbstract
    pageVariables?: PageVariableSchema[]
    pagePathParameter?: ArgumentsSchema[]
    pageQueryParameter?: ArgumentsSchema[]
    pageStateParameter?: StateSchema[]
    upstreamNodes?: FlowNode[]
    allUpstreamNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    viewType?: ViewType
    viewDsId?: string
    extraViewFields?: Field[]
    paramsDataSource?: DataSourceAbstract
    isUseImage?: boolean

    availableFieldBlocksInfo?: AvailableFieldBlockInfo[]
    availableFilterBlocksInfo?: AvailableFilterBlockInfo[]
    dataSourceList: DataSourceAbstract[]
    prefixName: string
    width?: string
    filterBlocks?: FilterBlockAbstract[]
    workflowList?: Workflow[]
    disabled?: boolean
    style?: React.CSSProperties
    disableUserOption?: boolean
    disableDataSourceOption?: boolean
    onMouseEnter?: (blockId: string) => void
    onMouseLeave?: () => void
}

const ParamsBox = styled.div`
    display: flex;
    flex-flow: row nowrap;
    flex: 1;
    white-space: nowrap;
    gap: 8px;
`
const SCxInput = styled(Input)`
    input {
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            appearance: none !important;
            margin: 0;
        }
    }
`

export const ParamsVariable: React.FC<ParamsVariableProps> = ({
    field,
    formDsId,
    dataSource,
    pagePathParameter,
    pageQueryParameter,
    pageVariables,
    pageStateParameter,
    upstreamNodes,
    allUpstreamNodes,
    allParentActions,
    availableFieldBlocksInfo = [],
    availableFilterBlocksInfo = [],
    dataSourceList,
    prefixName,
    width,
    viewType,
    viewDsId,
    extraViewFields,
    paramsDataSource,
    isUseImage,
    filterBlocks,
    workflowList,
    disabled,
    disableUserOption,
    disableDataSourceOption,
    style,
    onMouseEnter,
    onMouseLeave
}) => {
    const { control } = useFormContext()
    const operator: string = useWatch({
        control,
        name: `${prefixName}.operator`
    })

    const realField = useMemo(() => {
        if (!field) {
            return
        }
        if ((field.type === 'formula' || field.type === 'aggregation') && field.innerType) {
            return {
                ...field,
                type: AggregationInnerTypeToFieldType[field.innerType]
            } as Field
        }
        return field
    }, [field])

    const viewOptions = useMemo(() => {
        if (paramsDataSource) {
            return
        }
        const viewDataSource = dataSourceList.find(item => item.id === viewDsId)
        const viewOption = getViewVariableOption({
            dataSource: viewDataSource,
            viewType,
            extraFields: extraViewFields
        })
        return viewOption ? [viewOption] : undefined
    }, [dataSourceList, extraViewFields, paramsDataSource, viewDsId, viewType])

    const inputOption = useMemo(() => {
        if (paramsDataSource || availableFieldBlocksInfo.length === 0) {
            return
        }
        return getInputVariableOption(availableFieldBlocksInfo, dataSourceList, {
            onMouseEnter,
            onMouseLeave
        })
    }, [dataSourceList, availableFieldBlocksInfo, onMouseEnter, onMouseLeave, paramsDataSource])

    const filterOption = useMemo(() => {
        if (availableFilterBlocksInfo.length === 0) {
            return
        }
        return getFilterControllerVariableOption(availableFilterBlocksInfo, {
            onMouseEnter,
            onMouseLeave
        })
    }, [availableFilterBlocksInfo, onMouseEnter, onMouseLeave])

    const formOption = useMemo(() => {
        if (paramsDataSource) {
            return
        }
        const formDataSource = dataSourceList.find(item => item.id === formDsId)
        return getFormDatasourceOptions({
            dataSource: formDataSource
        })
    }, [dataSourceList, formDsId, paramsDataSource])

    const userOption = useMemo(() => {
        if (paramsDataSource || disableUserOption) {
            return
        }
        const userDataSource = dataSourceList.find(item => item.id === USER_DATASOURCE)
        return getUserDatasourceOptions({
            dataSource: userDataSource
        })
    }, [dataSourceList, disableUserOption, paramsDataSource])

    const datasourceOption = useMemo(() => {
        if (disableDataSourceOption) {
            return
        }
        return getDefaultDataSourceOptions(paramsDataSource ?? dataSource)
    }, [dataSource, disableDataSourceOption, paramsDataSource])

    const mode = useMemo(() => {
        return filterModeIndex[operator]
    }, [operator])

    const pageOptions = useMemo(
        () =>
            paramsDataSource
                ? undefined
                : getPageParameterAndVariableOptions({
                      pagePathParameter,
                      pageQueryParameter,
                      pageVariables,
                      pageStateParameter,
                      dataSourceList
                  }),
        [dataSourceList, pagePathParameter, pageQueryParameter, pageStateParameter, pageVariables, paramsDataSource]
    )
    const upstreamOptions = useMemo(() => {
        if (paramsDataSource) {
            return
        }
        if (allParentActions) {
            return getUpstreamActionsOptions({
                workflowList: workflowList || [],
                allParentActions,
                dataSourceList
            })
        }
        return getUpstreamNodesOptions({
            workflowList: workflowList || [],
            upstreamNodes,
            allUpstreamNodes,
            dataSourceList
        })
    }, [allParentActions, workflowList, upstreamNodes, allUpstreamNodes, dataSourceList, paramsDataSource])

    const imageOption = useMemo(() => {
        if (isUseImage) {
            return getImageVariableOption()
        }
    }, [isUseImage])

    const innerTypes: TypeInstanceMap[] = useMemo(() => {
        if (!field?.innerType) {
            return []
        }
        if (field.innerType === 'ARRAY' && ['contains', 'notContains'].includes(operator)) {
            return ['TEXT', 'NUMBER', 'ARRAY']
        }
        return [field.innerType]
    }, [field?.innerType, operator])

    const renderParams = useCallback(
        (ctlField: ControllerRenderProps<FieldValues, `${string}.paramList`>) => {
            const [firstParam = DEFAULT_VALUE_VARIABLE, secondParam = DEFAULT_VALUE_VARIABLE] = ctlField?.value || [
                DEFAULT_VALUE_VARIABLE,
                DEFAULT_VALUE_VARIABLE
            ]

            if (operator === FILTER_OPERATOR) {
                return (
                    <VariableSelect
                        field={realField}
                        innerTypes={innerTypes}
                        disablePexels
                        value={firstParam}
                        disabled={!realField || disabled}
                        style={{ width, whiteSpace: 'nowrap' }}
                        placeholder="请选择"
                        width={width}
                        onChange={val => {
                            ctlField.onChange([val])
                            onMouseLeave?.()
                        }}
                    />
                )
            }
            const dateOption = realField?.type === 'date' ? getDateVariableOption(systemMapList[mode ?? 0]) : undefined

            if (mode === 0 && realField?.innerType === 'DATE') {
                return (
                    <ParamsBox>
                        <VariableSelect
                            key={`${prefixName}.paramList`}
                            field={realField}
                            innerTypes={innerTypes}
                            multiple={false}
                            datasourceOption={datasourceOption}
                            pageOptions={pageOptions}
                            upstreamOptions={upstreamOptions}
                            viewOptions={viewOptions}
                            value={firstParam}
                            inputOption={inputOption}
                            filterOption={filterOption}
                            dateOption={dateOption}
                            formOption={formOption}
                            userOption={userOption}
                            imageOption={imageOption}
                            disabled={disabled}
                            width={width}
                            disablePexels
                            style={{ flex: 1, maxWidth: FILTER_ITEM_MAX_WIDTH }}
                            // style={{ flex: 1, minWidth: 160 }}
                            onChange={val => {
                                if (
                                    val &&
                                    val.type === VariableType.SYSTEM &&
                                    needNumberParamSystemVariableList.includes(val.systemVariable?.value as string)
                                ) {
                                    ctlField.onChange([val, secondParam])
                                    return
                                }
                                ctlField.onChange([val])
                            }}
                        />
                        {needNumberParamSystemVariableList.includes(firstParam?.systemVariable?.value) && (
                            <SCxInput
                                style={{ minWidth: 135 }}
                                key={`${prefixName}.paramList2`}
                                {...ctlField}
                                disabled={!realField || disabled}
                                placeholder="输入天数，例如“1”"
                                value={secondParam?.valueVariable?.value}
                                type="number"
                                min={1}
                                onChange={ev => {
                                    const { value: newValue } = ev.target
                                    ctlField.onChange([
                                        firstParam,
                                        {
                                            type: VariableType.VALUE,
                                            valueVariable: { value: newValue }
                                        }
                                    ])
                                }}
                            />
                        )}
                    </ParamsBox>
                )
            }
            return (
                <VariableSelect
                    placeholder={placeholderFromInnerType[realField?.innerType || 'NULL']}
                    field={realField}
                    innerTypes={innerTypes}
                    datasourceOption={datasourceOption}
                    pageOptions={pageOptions}
                    upstreamOptions={upstreamOptions}
                    viewOptions={viewOptions}
                    value={firstParam}
                    inputOption={inputOption}
                    filterOption={filterOption}
                    dateOption={dateOption}
                    userOption={userOption}
                    formOption={formOption}
                    imageOption={imageOption}
                    multiple={mode === 1}
                    disabled={!realField || disabled}
                    style={{ width, whiteSpace: 'nowrap', minWidth: 120, maxWidth: FILTER_ITEM_MAX_WIDTH }}
                    width={width}
                    onChange={val => {
                        ctlField.onChange([val])
                        onMouseLeave?.()
                    }}
                    disablePexels
                />
            )
        },
        [
            operator,
            realField,
            mode,
            innerTypes,
            datasourceOption,
            pageOptions,
            upstreamOptions,
            viewOptions,
            inputOption,
            filterOption,
            userOption,
            formOption,
            imageOption,
            disabled,
            width,
            onMouseLeave,
            prefixName
        ]
    )

    return (
        <Controller
            name={`${prefixName}.paramList`}
            control={control}
            render={({ field: ctlField }) => {
                if (operator === 'isEmpty' || operator === 'isNotEmpty') {
                    return <></>
                }
                return (
                    <SC.Box key={`${prefixName}.paramList`} style={style}>
                        {renderParams(ctlField)}
                    </SC.Box>
                )
            }}
        />
    )
}
